// =============================================================================
// src/app/owner-identity.ts — Identidade do GABINETE acompanhado (o "dono" do
//   observatório). Fonte única, resolvida em runtime a partir do backend (tenant)
//   com fallback estático para o piloto Omar Aziz.
//
// Por que aqui (app layer): a topbar (chrome), os takeovers (features) e o briefing
//   todos exibiam a persona — antes hardcoded como a vereadora fictícia "Gab. Ver.
//   Helena Braga" em ~10 lugares. Centralizamos num resolver (DRY) que segue o
//   VITE_SENTINEL_CLIENT_ID: main.ts hidrata `setOwnerIdentity` a partir do
//   app_client/monitored_actor (src/data/identity-client.ts) ANTES de montar o DOM,
//   então os render() chamam resolveOwnerIdentity() e já veem o tenant correto.
//
// O CARGO ("Sen./Senado") NÃO vem do backend (app_client.entity_type='politician'
//   genérico) — é config de apresentação do piloto. Revisar quando o backend expor
//   um campo de cargo/office. Nome e partido SÃO dinâmicos (backend).
// =============================================================================

/** Identidade do dono pronta para exibição (chip da topbar, cabeçalhos, briefing). */
export interface OwnerIdentity {
  readonly name: string;
  readonly meta: string;
  readonly initials: string;
  readonly party: string;
}

/** Campos crus de identidade vindos do backend (src/data/identity-client.ts). */
export interface OwnerIdentityLive {
  readonly name: string;
  readonly party: string | null;
}

/** Cargo de apresentação do piloto — backend não carrega office; ver topo do arquivo. */
const OWNER_TITLE = { prefix: 'Sen.', scope: 'Senado', uf: 'AM' } as const;

/** Fallback (sem backend / falha de fetch): o piloto Omar Aziz, PSD-AM. */
export const OWNER_IDENTITY_FALLBACK: OwnerIdentity = {
  name: 'Sen. Omar Aziz',
  meta: 'Senado · PSD-AM',
  initials: 'OA',
  party: 'PSD',
};

let currentOwnerIdentity: OwnerIdentity = OWNER_IDENTITY_FALLBACK;

/**
 * Iniciais (primeira + última palavra) de um nome para avatares.
 *
 * @example initialsOf('Omar Aziz') // 'OA'
 */
export function initialsOf(fullName: string): string {
  const parts = fullName.split(' ').filter(Boolean);
  const first = parts[0]?.charAt(0) ?? '';
  /* v8 ignore next -- fallback exigido por noUncheckedIndexedAccess; length>1 garante parts[last] */
  const last = parts.length > 1 ? parts[parts.length - 1]?.charAt(0) ?? '' : '';
  return (first + last).toUpperCase();
}

/** Identidade vigente do dono — fallback estático até main.ts hidratar do backend. */
export function resolveOwnerIdentity(): OwnerIdentity {
  return currentOwnerIdentity;
}

/** Define a identidade vigente (chamado por main.ts após o fetch do backend). */
export function setOwnerIdentity(identity: OwnerIdentity): void {
  currentOwnerIdentity = identity;
}

/** Restaura o fallback — usado pelos testes para isolar o singleton de módulo. */
export function __resetOwnerIdentityForTests(): void {
  currentOwnerIdentity = OWNER_IDENTITY_FALLBACK;
}

/**
 * Compõe a identidade de exibição a partir dos campos crus do backend. Nome e
 * partido são dinâmicos; o cargo ("Sen./Senado") vem da config do piloto (OWNER_TITLE).
 *
 * @example composeOwnerIdentity({ name: 'Omar Aziz', party: 'PSD' })
 *   // { name: 'Sen. Omar Aziz', meta: 'Senado · PSD-AM', initials: 'OA', party: 'PSD' }
 */
export function composeOwnerIdentity(live: OwnerIdentityLive): OwnerIdentity {
  const party = live.party ?? OWNER_IDENTITY_FALLBACK.party;
  return {
    name: `${OWNER_TITLE.prefix} ${live.name}`,
    meta: `${OWNER_TITLE.scope} · ${party}-${OWNER_TITLE.uf}`,
    initials: initialsOf(live.name),
    party,
  };
}
